import {ETextParamType_SetDateNow} from './const';
import {hel_show_fatal_error_dialog} from './widgetErrorDialog';

import type {setstateForHeliModule} from '../logics/globals';

/** @see https://github.com/emscripten-core/emscripten/blob/main/src/lib/libwebgl.js */
export interface EmscriptenWebGLContextAttributes extends WebGLContextAttributes {
  majorVersion?: number;
  minorVersion?: number;
  enableExtensionsByDefault?: boolean;
  explicitSwapControl?: boolean;
  preferLowPowerToHighPerformance?: boolean;
}

declare global {
  /* eslint-disable no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須 */
  var HeliModule: EmscriptenModule & {
    ccall: typeof ccall | undefined;
    getValue: typeof getValue;
    setValue: typeof setValue;
    /** @see https://github.com/emscripten-core/emscripten/blob/90969999f595818bfc0373b40115ef728de1ee42/src/library_html5_webgl.js#L226 */
    ctx: WebGLRenderingContext | undefined | null;
    calledRun: boolean | undefined;
    /**
     * @remarks TypeScript層で生やされ、C++層から呼び出されるメソッド。
     * @see https://github.com/VRHIKKY/WorldViewer/blob/970cafb2d9eb0141a6b400c4487a891972e61550/Heliodor/Source/App/CApp.cpp#L192
     * @see https://github.com/VRHIKKY/WorldViewer/blob/970cafb2d9eb0141a6b400c4487a891972e61550/Heliodor/Source/Specific/EmscriptenApp/Main.cpp#L215
     */
    setstate?: typeof setstateForHeliModule;
  };

  /** @see https://github.com/emscripten-core/emscripten/blob/main/src/embind/emval.js */
  var Emval: {
    toValue: (handle: number) => unknown;
    toHandle: (value: unknown) => number;
  };

  /** @see https://github.com/emscripten-core/emscripten/blob/main/src/lib/libwebgl.js */
  var GL: {
    registerContext: (
      ctx: WebGLRenderingContext,
      webGLContextAttributes: EmscriptenWebGLContextAttributes & {
        majorVersion?: number;
        minorVersion?: number;
        enableExtensionsByDefault?: boolean;
        explicitSwapControl?: boolean;
        preferLowPowerToHighPerformance?: boolean;
      }
    ) => number;
    makeContextCurrent: (contextHandle: number) => boolean;
    getContext: (contextHandle: number) => WebGLRenderingContext | null;
    deleteContext: (contextHandle: number) => void;
  };

  /**
   * @remarks
   * このグローバル変数がまだ `undefined` の場合、{@link HeliModule.ccall } でエラーが発生する。
   * その場合でも、変数としては `undefined` が代入されているため、`'wasmExports' in globalThis` では判定できない。
   */
  var wasmExports: unknown;

  /**
   * @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L27-L38
   * @param type 整数。
   * @param text
   */
  var cef_SetText: (type: number, text: string) => void | undefined;
  /**
   * @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L39-L48
   * @param type 整数。
   * @param text
   */
  var cef_PostMessage: (type: number, text: string) => void | undefined;
  /** @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L49-L57 */
  var cef_ReceiveSkywayData: (data: string) => void | undefined;
  /** @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L58-L67 */
  var cef_OnNetReceivePosJoin: (id: string, data: string) => void | undefined;
  /** @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L68-L77 */
  var cef_OnNetReceivePosLeave: (id: string, data: string) => void | undefined;
  /** @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L78-L87 */
  var cef_OnNetReceiveVoiceJoin: (id: string, data: string) => void | undefined;
  /** @see https://github.com/VRHIKKY/WorldViewer/blob/8be1353e82a8cebacf5b56472f6125b941accde0/Heliodor/Source/Specific/CEF/CV8Handler.cpp#L88-L97 */
  var cef_OnNetReceiveVoiceLeave: (id: string, data: string) => void | undefined;
  /* eslint-enable */
}

export const requestPreBootStrapGameEngine = () => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall === undefined) {
    return;
  }
  HeliModule.ccall('PreStart', null, [], []);
};

/**
 * COMMON
 */
export const requestToDrawFrame = () => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    hel_setText(ETextParamType_SetDateNow, Date.now().toString());

    // ToDo: パフォーマンスに影響があったらこのtry-catch文は無くして元に戻す
    try {
      HeliModule.ccall('DrawFrame', null, [], []);
    } catch (error: any) {
      hel_show_fatal_error_dialog('HEL007001001', '[Cpp Error] ' + error.message);
      throw error;
    }
  }
};

/**
 * COMMON
 * requestProcedureCallFor
 * @param procedureId - Type
 * @param order - Text
 */
export const hel_setText = (procedureId: number, order: string): number => {
  if (globalThis.cef_SetText !== undefined) {
    globalThis.cef_SetText(procedureId, order);

    // CEFが別プロセスで動いており、現時点で同期的に戻り値を取得出来ないのでひとまず0を返す
    return 0;
  } else if (HeliModule.ccall !== undefined) {
    // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
    if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return 0;

    return HeliModule.ccall('SetText', 'number', ['number', 'string'], [procedureId, order]);
  } else {
    return 0;
  }
};

//
export const requestProcedurePostFor = (procedureId: number, order: string): void => {
  if (globalThis.cef_PostMessage !== undefined) {
    globalThis.cef_PostMessage(procedureId, order);
  } else if (HeliModule.ccall !== undefined) {
    // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
    if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

    HeliModule.ccall('PostMessage', null, ['number', 'string'], [procedureId, order]);
  }
};
/**
 * COMMON
 * @param procedureId
 * @param ary_u8
 */
export const requestProcedureCallBinaryFor = (procedureId: number, ary_u8: Uint8Array): number => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return 0;

  if (HeliModule.ccall !== undefined) {
    return HeliModule.ccall(
      'SetBinary',
      'number',
      ['number', 'array', 'number'],
      [procedureId, ary_u8, ary_u8.length]
    );
  }

  return 0;
};

/**
 * Skyway
 * @param data
 */
export const requestSendingReceivedPacketsFromSkyway = (cmd: string, id: string, param: string) => {
  const data = cmd + ':' + id + ':' + param;
  if (globalThis.cef_ReceiveSkywayData !== undefined) {
    globalThis.cef_ReceiveSkywayData(data);
  } else if (HeliModule.ccall !== undefined) {
    // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
    if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

    HeliModule.ccall('ReceiveSkywayData', null, ['string'], [data]);
  }
};

/**
 * XR
 * @param vrcameradata
 */
export const requestToSetVrCameraPointer = (vrcameradata: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('SetVRCameraValue', null, ['number'], [vrcameradata]);
  }
};

/**
 * XR
 * @param data
 */
export const requestSetVrPointer = (data: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('SetVRValue', null, ['number'], [data]);
  }
};

/**
 * XR
 * @version 2.9.0
 * @param pose
 */
export const requestSetVrPosePointer = (pose: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('SetVRPoseValue', null, ['number'], [pose]);
  }
};

/**
 * XR。ctx経由
 */
export const requestToBindFrameBufferOf = (glLayer: XRWebGLLayer | undefined) => {
  if (HeliModule.ctx !== undefined && HeliModule.ctx !== null && glLayer !== undefined) {
    HeliModule.ctx.bindFramebuffer(HeliModule.ctx.FRAMEBUFFER, glLayer.framebuffer);
  }
};

/**
 * XR。ctx経由
 */
export const requestToReleaseBoundFrameBuffer = () => {
  if (HeliModule.ctx !== undefined && HeliModule.ctx !== null) {
    HeliModule.ctx.bindFramebuffer(HeliModule.ctx.FRAMEBUFFER, null);
  }
};

/**
 * XR
 * @param cameradata
 */
export const requestIfCameraFpsValueAvailable = (cameradata: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return false;

  if (HeliModule.ccall !== undefined) {
    return HeliModule.ccall('GetFPSCameraValue', 'boolean', ['number'], [cameradata]);
  }
  return false;
};

/**
 * XR
 * @param target
 */
export const requestToGetValueOf = (target: number): number => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return 0;

  if (HeliModule.ccall !== undefined) {
    return HeliModule.getValue(target, 'float');
  }
  return 0;
};

/**
 * XR
 * @param controllerNum
 * @param activeData
 * @param controllerData {float}
 * @param gamePadData {float}
 */
export const requestVRControllerValue = (
  controllerNum: number,
  activeData: number,
  controllerData: number,
  gamePadData: number
): boolean => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return false;

  if (HeliModule.ccall !== undefined) {
    return HeliModule.ccall(
      'SetVRControllerValues',
      'boolean',
      ['number', 'number', 'number', 'number'],
      [controllerNum, activeData, controllerData, gamePadData]
    );
  }
  return false;
};

/**
 * Switch to WebXR
 * @param width
 * @param height
 * @returns
 */
export const switchToVR = (width: number, height: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return false;

  if (HeliModule.ccall !== undefined) {
    return HeliModule.ccall('SwitchToVR', null, ['number', 'number'], [width, height]);
  }
  return false;
};

/**
 * Switch to None WebXR
 * @param width
 * @param height
 * @returns
 */
export const switchToNoneVR = () => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return false;

  if (HeliModule.ccall !== undefined) {
    return HeliModule.ccall('SwitchToNoneVR', null, [], []);
  }
  return false;
};

/**
 * DEBUG
 * @param text
 */
export const requestDebugOutput = (text: string) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('DebugOutput', null, ['string'], [text]);
  }
};

/**
 * DEBUG
 * @param pointer
 * @param length
 */
export const requestMemoryBytesOutput = (pointer: number[], length: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('DebugMemoryByteOutput', null, ['array', 'number'], [pointer, length]);
  }
};

/**
 * [ GUI ]
 */
export const requestResizeWindow = (mainCanvas: HTMLCanvasElement) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined && mainCanvas !== undefined) {
    const width = Math.floor(mainCanvas.clientWidth * window.devicePixelRatio);
    const height = Math.floor(mainCanvas.clientHeight * window.devicePixelRatio);

    HeliModule.ccall(
      'ResizeWindow',
      null,
      ['number', 'number', 'number', 'number'],
      [mainCanvas.width, mainCanvas.height, width, height]
    );
  }
};

/**
 * [ FILE ]
 * @param file
 */
export const requestOnDropFilesBegin = (file: any) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('OnDropFilesBeginFromJS', null, ['string'], [file.name]);
  }
};

/**
 * [ FILE ]
 * @param evt
 */
export const requestOnDropFilesProgress = (evt: any) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('OnDropFilesProgressFromJS', null, ['number'], [evt.loaded / evt.total]);
  }
};

/**
 * [ FILE ]
 * @param file
 * @param ary_u8
 * @param data
 */
export const requestOnDropFiles = (file: any, ary_u8: Uint8Array, data: any) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall(
      'OnDropFilesFromJS',
      null,
      ['string', 'number', 'number'],
      [file.name, data, ary_u8.length]
    );
  }
};

/**
 * ウィンドウアクティブ化・非アクティブ化
 */
export const requestOnWindowActivate = (isActivate: boolean) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('OnWindowActivate', null, ['boolean'], [isActivate]);
  }
};

/**
 * アンロード直前
 */
export const requestOnBeforeUnload = () => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('OnBeforeUnload', null, [], []);
  }
};

/**
 * アンロード
 */
export const requestOnUnload = () => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('OnUnload', null, [], []);
  }
};

/**
 * HeliScriptのコンポーネント呼び出し
 * @remarks
 * 本関数はグローバル関数用の共通処理であり、それ以外でのTypeScript層内での呼び出しは禁止。代わりに {@link hel_DispatchEvent} を利用する。
 */
export const callComponentMethod = (
  isScene: boolean,
  itemName: string,
  componentName: string,
  methodName: string,
  params: any[]
): number | string | boolean | null => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return null;

  if (HeliModule.ccall !== undefined) {
    // 戻り値を入れてもらう配列 (要素を1個埋めておく)
    const resultArray: any[] = [undefined];

    const success: boolean = HeliModule.ccall(
      'CallComponentMethod',
      'boolean',
      [`number`, 'boolean', 'string', 'string', 'string', 'number'],
      [
        globalThis.Emval.toHandle(resultArray),
        isScene,
        itemName,
        componentName,
        methodName,
        globalThis.Emval.toHandle(params)
      ]
    );

    if (success) {
      return resultArray[0];
    }
  }
  return null;
};

/**
 * HeliScriptのイベントリスナーへイベント発火
 * @see https://hikky.atlassian.net/wiki/spaces/VKETCLOUD/pages/800031291/HeliScript#%E3%82%A4%E3%83%99%E3%83%B3%E3%83%88%E3%83%AA%E3%82%B9%E3%83%8A%E3%83%BC
 */
export const hel_DispatchEvent = (eventType: string, param: string): void => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('DispatchHSEvent', null, ['string', 'string'], [eventType, param]);
  }
};

/**
 * [ HARDWARE ]
 * @param targetLength
 */
export const requestMemoryAllocate = (targetLength: number) => {
  return HeliModule._malloc(targetLength);
};

/**
 * [ HARDWARE ]
 * @param ary_u8
 * @param data
 */
export const requestSetDataToHeap = (ary_u8: Uint8Array, data: any) => {
  HeliModule.HEAPU8.set(ary_u8, data);
};

/**
 * [ HARDWARE ]
 * @param data
 */
export const requestReleaseMemoryFor = (data: any) => {
  HeliModule._free(data);
};

/**
 * [ HARDWARE ]
 * @param data
 */
export const sendGamepadData = (
  button1: number,
  button2: number,
  button3: number,
  button4: number,
  button5: number,
  button6: number,
  button7: number,
  button8: number,
  button9: number,
  button10: number,
  button11: number,
  button12: number,
  button13: number,
  button14: number,
  button15: number,
  button16: number,
  axis1: number,
  axis2: number,
  axis3: number,
  axis4: number
) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall(
      'ReceiveGamepadData',
      null,
      [
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number',
        'number'
      ],
      [
        button1,
        button2,
        button3,
        button4,
        button5,
        button6,
        button7,
        button8,
        button9,
        button10,
        button11,
        button12,
        button13,
        button14,
        button15,
        button16,
        axis1,
        axis2,
        axis3,
        axis4
      ]
    );
  }
};

export const setServerTime = (dateTime: number) => {
  // Moduleが初期化されていない時にccallするとundefinedの例外が発生するので抑制する
  if (HeliModule.calledRun === undefined || !HeliModule.calledRun) return;

  // ローカルの現在時刻との差分を、ミリ秒単位で取得
  const msecOffset: number = new Date().getTime() - dateTime;
  // Heliodor側に設定
  if (HeliModule.ccall !== undefined) {
    HeliModule.ccall('SetServerTimeOffset', null, ['number'], [msecOffset]);
  }
};

export const hel_postMessage = (Type: number, Text: string): void => {
  requestProcedurePostFor(Type, Text);
};
